import React, { ReactNode, useState } from 'react'
import { Box, Collapse, Stack, SvgIcon, SxProps, Theme, Typography } from '@mui/material'
import pluralize from 'pluralize'
import { ReactComponent as ArrowDownIcon } from '../../../assets/images/arrows/down-bold.svg'
import { CommentsViewFilterClearButton } from './CommentsViewFilterField'
import { ConditionalTooltipWrapper } from '../../../shared/components/ConditionalTooltipWrapper'

type CommentsViewFilterAccordionSectionProps = {
    title: string
    appliedCount?: number
    onClear?: () => void
    defaultExpanded?: boolean
    isCollapsible?: boolean
    headerActions?: ReactNode
    children: ReactNode
    containerSx?: SxProps<Theme>
    contentSx?: SxProps<Theme>
}

const CommentsViewFilterAccordionSection = ({
    title,
    appliedCount = 0,
    onClear,
    defaultExpanded = false,
    isCollapsible = true,
    headerActions,
    children,
    containerSx,
    contentSx,
}: CommentsViewFilterAccordionSectionProps) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)
    const hasAppliedFilters = appliedCount > 0

    return (
        <Stack gap={1} width="100%" sx={containerSx}>
            <Stack
                direction="row"
                alignItems="center"
                py={1}
                gap={1.25}
                sx={{ cursor: isCollapsible ? 'pointer' : 'default' }}
                width="max-content"
                onClick={() => {
                    if (!isCollapsible) {
                        return
                    }

                    setIsExpanded((currentValue) => !currentValue)
                }}
            >
                <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
                    <ConditionalTooltipWrapper
                        condition={hasAppliedFilters}
                        placement="top-start"
                        arrow
                        title={
                            <Typography variant={'14x600'} color={'figma.primary'}>
                                {`${appliedCount} ${pluralize('filter', appliedCount)} applied`}
                            </Typography>
                        }
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: 'figma.grayscale.contentWrapper',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: '6px',
                                    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                                },
                            },
                            arrow: {
                                sx: {
                                    color: 'figma.grayscale.contentWrapper',
                                },
                            },
                        }}
                    >
                        <Stack direction="row" alignItems="center" gap={1.25}>
                            <Typography
                                variant="18x600"
                                color={hasAppliedFilters ? 'figma.primary' : 'figma.grayscale.textHier1'}
                            >
                                {title}
                            </Typography>
                        </Stack>
                    </ConditionalTooltipWrapper>
                    {isCollapsible ? (
                        <SvgIcon
                            component={ArrowDownIcon}
                            inheritViewBox
                            color="inherit"
                            sx={{
                                fontSize: '16px',
                                color: 'figma.grayscale.textHier2',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            }}
                        />
                    ) : null}
                    {headerActions ? (
                        <Box
                            onClick={(event) => {
                                event.stopPropagation()
                            }}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            {headerActions}
                        </Box>
                    ) : null}
                    {hasAppliedFilters && onClear ? <CommentsViewFilterClearButton onClick={onClear} /> : null}
                </Stack>
            </Stack>
            <Collapse in={isExpanded} timeout={200} unmountOnExit>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={3} flexWrap="wrap" pb={1} pt={0.5} sx={contentSx}>
                    {children}
                </Stack>
            </Collapse>
        </Stack>
    )
}

export default CommentsViewFilterAccordionSection
